import { strings } from "@perceptron/constants";
import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { formatString } from "@perceptron/utils/string";
import { MediaKind, MediaSetContext } from "@perceptron/utils/types";
import { Editor } from "@tiptap/core";
import { Transaction } from "@tiptap/pm/state";
import { intervalToDuration } from "date-fns";
import { toast } from "sonner";

import { MediaFactory } from "@demo/libs/media-factory";
import { MediaUploadService } from "@demo/libs/media-upload";
import { MediaCapability } from "@demo/shared/types";
import { useMediaStore } from "@demo/store/media";
import { useModelStore } from "@demo/store/model";
import { useUploadProgressStore } from "@demo/store/upload-progress";
import { Role } from "@demo/types/base";
import { insertMediaSet } from "@demo/utils/insert-media-set";
import { validateVideoDuration, VIDEO_CONSTRAINTS } from "@demo/utils/video-duration";

const convertSecondsToMinutes = (seconds: number): number => {
  const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
  return duration.minutes ?? 0;
};

const generateFileNamesList = (files: File[]): string => {
  return files.map((f) => f.name).join(", ");
};

/**
 * Shows toast error if model doesn't support the media type and returns empty array.
 * Returns original files array if model supports the type.
 */
const filterByModelCapability = (
  files: File[],
  modelSupportsType: boolean,
  modelName: string,
  mediaKind: MediaKind,
): File[] => {
  if (!modelSupportsType && files.length > 0) {
    toast.error(
      formatString(
        strings.fileHandler.errors.modelDoesNotSupportMediaType,
        generateFileNamesList(files),
        modelName,
        mediaKind,
      ),
    );
    return [];
  }
  return files;
};

export async function validateFiles(files: File[]) {
  // Get model capabilities to check what media types are supported
  const selectedModel = useModelStore.getState().selectedModel;

  const modelMediaCapabilities: MediaCapability[] = selectedModel.capabilities.media;

  // Step 1: Single-pass categorization by MIME type
  // Each file is checked exactly once and placed into one of three buckets
  const imageFiles: File[] = [];
  const videoFiles: File[] = [];
  const unsupportedFiles: File[] = [];

  const acceptedImageTypes = MediaFactory.getAcceptedMimeTypes(MediaKind.Image);
  const acceptedVideoTypes = MediaFactory.getAcceptedMimeTypes(MediaKind.Video);

  for (const file of files) {
    if (acceptedImageTypes.includes(file.type)) {
      imageFiles.push(file);
    } else if (acceptedVideoTypes.includes(file.type)) {
      videoFiles.push(file);
    } else {
      unsupportedFiles.push(file);
    }
  }

  // Step 2: Show error for unsupported formats (immediately, no re-filtering needed)
  if (unsupportedFiles.length > 0) {
    toast.error(formatUnsupportedFilesError(unsupportedFiles));
  }

  // Step 3: Filter out media types not supported by the current model
  const modelSupportsImages = modelMediaCapabilities.includes(MediaKind.Image);
  const modelSupportsVideos = modelMediaCapabilities.includes(MediaKind.Video);

  let validImageFiles = filterByModelCapability(
    imageFiles,
    modelSupportsImages,
    selectedModel.publicName,
    MediaKind.Image,
  );
  let validVideoFiles = filterByModelCapability(
    videoFiles,
    modelSupportsVideos,
    selectedModel.publicName,
    MediaKind.Video,
  );

  // Step 4: Check for files that are too large
  const oversizedImageFiles = validImageFiles.filter((f) => f.size > MediaFactory.getMaxFileSize(MediaKind.Image));
  const oversizedVideoFiles = validVideoFiles.filter((f) => f.size > MediaFactory.getMaxFileSize(MediaKind.Video));

  if (oversizedImageFiles.length > 0) {
    const fileNames = generateFileNamesList(oversizedImageFiles);
    const maxSizeMB = MediaFactory.getMaxFileSize(MediaKind.Image) / (1024 * 1024);
    toast.error(formatString(strings.fileHandler.errors.imagesTooLarge, fileNames, maxSizeMB.toString()));
  }

  if (oversizedVideoFiles.length > 0) {
    const fileNames = generateFileNamesList(oversizedVideoFiles);
    const maxSizeMB = MediaFactory.getMaxFileSize(MediaKind.Video) / (1024 * 1024);
    toast.error(formatString(strings.fileHandler.errors.videosTooLarge, fileNames, maxSizeMB.toString()));
  }

  // Filter out oversized files from the validated files
  validImageFiles = validImageFiles.filter((f) => f.size <= MediaFactory.getMaxFileSize(MediaKind.Image));
  validVideoFiles = validVideoFiles.filter((f) => f.size <= MediaFactory.getMaxFileSize(MediaKind.Video));

  // Step 5: Validate video duration
  if (validVideoFiles.length > 0) {
    const { validFiles, tooLongFiles } = await validateVideoDuration(
      validVideoFiles,
      VIDEO_CONSTRAINTS.MAX_DURATION_SECONDS,
    );

    if (tooLongFiles.length > 0) {
      const fileNames = generateFileNamesList(tooLongFiles);
      toast.error(
        formatString(
          strings.fileHandler.errors.videosTooLong,
          fileNames,
          convertSecondsToMinutes(VIDEO_CONSTRAINTS.MAX_DURATION_SECONDS).toString(),
        ),
      );
    }

    // Only include videos that passed duration validation
    validVideoFiles = validFiles;
  }

  if (validImageFiles.length === 0 && validVideoFiles.length === 0) {
    return { validatedImageFiles: [], validatedVideoFiles: [] }; // No valid files
  }

  return { validatedImageFiles: validImageFiles, validatedVideoFiles: validVideoFiles };
}

export async function handleFileDropToExistingMediaSetContainer(editor: Editor, files: File[], mediaSetId: string) {
  const validatedFiles = await validateFilesForUpload(files);
  await processValidatedFiles(editor, validatedFiles, { mediaSetId });
}

export async function handleFileDrop(context: MediaSetContext, editor: Editor, files: File[], pos: number) {
  const { validatedFiles, position } = await validateFilesAtMappedPosition(editor, pos, files);

  if (validatedFiles.length === 0) return;

  // Try to find if we're dropping on an existing MediaSetContainer
  const { state } = editor;
  const $pos = state.doc.resolve(position);

  // Check the node at the position and its parent
  let targetNode = null;

  // Check if we're directly on a MediaSetContainer
  const nodeAt = state.doc.nodeAt(position);
  if (nodeAt && nodeAt.type.name === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER) {
    targetNode = nodeAt;
  } else {
    // Check if we're inside a MediaSetContainer
    for (let depth = $pos.depth; depth >= 0; depth--) {
      const node = $pos.node(depth);
      if (node.type.name === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER) {
        targetNode = node;
        break;
      }
    }
  }

  if (targetNode) {
    // Add all valid files to existing container (unified media support)
    await processValidatedFiles(editor, validatedFiles, { mediaSetId: targetNode.attrs.id });
  } else {
    // Create a single new container for all files
    await processValidatedFiles(editor, validatedFiles, { position });
  }
}

export async function handleFilePaste(context: MediaSetContext, editor: Editor, files: File[]) {
  const { validatedFiles, position } = await validateFilesAtMappedPosition(editor, editor.state.selection.head, files);
  if (validatedFiles.length === 0) return;

  const previousSet = findPreviousMediaSetContainer(editor, position);
  const previousSetId = previousSet?.attrs.id;
  const storedSet =
    typeof previousSetId === "string"
      ? useMediaStore.getState().mediaSets?.find((set) => set.id === previousSetId)
      : undefined;
  const hasMedia =
    (Array.isArray(previousSet?.attrs.mediaIds) && previousSet.attrs.mediaIds.length > 0) ||
    (storedSet?.media.length ?? 0) > 0;
  const hasUploadInProgress =
    typeof previousSetId === "string" &&
    (useUploadProgressStore.getState().getUploadProgress(previousSetId)?.isUploading ?? false);

  if (previousSet && typeof previousSetId === "string" && !hasMedia && !hasUploadInProgress) {
    await processValidatedFiles(editor, validatedFiles, { mediaSetId: previousSetId });
    return;
  }

  await processValidatedFiles(editor, validatedFiles, { position });
}

async function validateFilesAtMappedPosition(editor: Editor, initialPosition: number, files: File[]) {
  let position = initialPosition;
  const mapThroughTransaction = ({ transaction }: { transaction: Transaction }) => {
    position = transaction.mapping.map(position, 1);
  };

  editor.on("transaction", mapThroughTransaction);
  try {
    const validatedFiles = await validateFilesForUpload(files);
    return {
      validatedFiles,
      position: Math.max(0, Math.min(position, editor.state.doc.content.size)),
    };
  } finally {
    editor.off("transaction", mapThroughTransaction);
  }
}

async function validateFilesForUpload(files: File[]) {
  const { validatedImageFiles, validatedVideoFiles } = await validateFiles(files);
  return [...validatedImageFiles, ...validatedVideoFiles];
}

async function processValidatedFiles(
  editor: Editor,
  files: File[],
  destination: { mediaSetId: string } | { position: number },
) {
  if (files.length === 0) return;

  if ("mediaSetId" in destination) {
    await MediaUploadService.processMediaFiles(files, destination.mediaSetId, editor);
    return;
  }

  await createNewMediaContainer(editor, files, destination.position);
}

/** Finds the closest media set among the cursor's preceding siblings. */
function findPreviousMediaSetContainer(editor: Editor, pos: number) {
  const resolved = editor.state.doc.resolve(pos);

  // Walk out through the cursor's ancestors so preceding media sets inside a
  // list or quote are preferred over sets earlier in the outer document.
  for (let depth = resolved.depth; depth >= 0; depth--) {
    const parent = resolved.node(depth);
    if (typeof resolved.index !== "function" || typeof parent.child !== "function") return null;

    for (let index = resolved.index(depth) - 1; index >= 0; index--) {
      const node = parent.child(index);
      if (node.type.name === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER) {
        return node;
      }
    }
  }

  return null;
}

function formatUnsupportedFilesError(unsupportedFiles: File[]): string {
  // Extract lowercase file extensions from unsupported files
  const unsupportedExtensions = unsupportedFiles
    .map((file) => {
      const extension = file.name.split(".").pop();
      return extension ? extension.toLowerCase() : file.type;
    })
    .join(", ");

  // Get supported friendly names from MediaFactory
  const supportedFormats = MediaFactory.getAllFriendlyNames().join(", ");

  return formatString(strings.fileHandler.errors.unsupportedFiles, unsupportedExtensions, supportedFormats);
}

async function createNewMediaContainer(editor: Editor, files: File[], pos: number) {
  // Create a new draft media set and insert its container at the drop position
  // TODO: Role should be determined from context when MediaStore becomes independent of role
  const setId = insertMediaSet(editor, Role.USER, pos);
  if (!setId) return;

  // Process and add the files to the container
  await MediaUploadService.processMediaFiles(files, setId, editor);
}
